import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';

import {
  getReportingEditReportUrl,
  matchConsolidationsRoutePath,
  matchReportingInternalRenderReportUrl
} from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';

import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportView } from '@futrli/views/views/reports/ModifyReportView/ModifyReportView';
import { ReportTemplatesListView } from '@futrli/views/views/reports/ReportTemplatesListView/ReportTemplatesListView';

import { CreateReportSidebarMode } from './create-report-sidebar';
import { ReportCreatedScreen } from './screens/report-created-screen';
import { ReportCreatingScreen } from './screens/report-creating-screen';
import { ReportFormScreen } from './screens/report-form-screen';
import { trackCreateReport } from '../../tracking';
import { useCreateReportForm } from './hooks/useCreateReportForm';
import { useOrgRouteParams } from '../../../hooks/useOrgRouteParams/useOrgRouteParams';
import { usePredictFlags } from '../../../hooks/usePredictFlags';

export interface CreateReportWizardContainerProps {
  onClose: () => void;
  mode?: CreateReportSidebarMode;
}

type Screen = 'form' | 'creating' | 'created';

export const CreateReportWizardContainer = ({ onClose, mode = 'report' }: CreateReportWizardContainerProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const location = useLocation();
  const { hasSwitchLocaleEnabled } = usePredictFlags();
  const { template, dashboardTemplate } = useSidebarContext();

  const { workspaceId, organisationId } = useOrgRouteParams(location.pathname);

  const isConsolidation =
    !!matchConsolidationsRoutePath(location.pathname) ||
    !!matchReportingInternalRenderReportUrl(location.pathname, true);

  const view = useApiView(
    useMemo(() => new ModifyReportView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const templatesView = useApiView(
    useMemo(() => new ReportTemplatesListView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const isDashboard = mode === 'dashboard';

  // Form state
  const {
    reportName,
    setReportName,
    creationMethod,
    setCreationMethod,
    selectedTemplateId,
    setSelectedTemplateId,
    errors: validationErrors,
    validate,
    revalidate
  } = useCreateReportForm(mode);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape' | 'dashboard'>(
    isDashboard ? 'dashboard' : 'portrait'
  );

  const filteredWorkspaceTemplates = useMemo(
    () =>
      isDashboard
        ? templatesView.state.publishedWorkspaceDashboardTemplates ?? []
        : templatesView.state.publishedWorkspaceReportTemplates ?? [],
    [isDashboard, templatesView.state.publishedWorkspaceDashboardTemplates, templatesView.state.publishedWorkspaceReportTemplates]
  );

  const filteredFutrliTemplates = useMemo(
    () =>
      isDashboard
        ? templatesView.state.futrliDashboardTemplates ?? []
        : templatesView.state.futrliReportTemplates ?? [],
    [isDashboard, templatesView.state.futrliDashboardTemplates, templatesView.state.futrliReportTemplates]
  );

  // React to view state changes for create flow
  useEffect(() => {
    if (view.state.isCreating) {
      setScreen('creating');
    }
  }, [view.state.isCreating]);

  useEffect(() => {
    if (view.state.newReportId) {
      trackCreateReport(creationMethod === 'scratch' ? orientation : 'portrait', isConsolidation);
      setScreen('created');
    }
  }, [view.state.newReportId, creationMethod, orientation, isConsolidation]);

  useEffect(() => {
    if (view.state.errorCreating) {
      setError(
        intl.formatMessage({
          id: 'reporting.try_again',
          defaultMessage: 'Try again.'
        })
      );
      setScreen('form');
    }
  }, [view.state.errorCreating, intl]);

  const handleCreate = useCallback(() => {
    if (!validate()) {
      return;
    }

    setError(undefined);

    const templateIdToUse = creationMethod !== 'scratch' ? selectedTemplateId : undefined;

    view.createReport({
      name: reportName.trim() || 'untitled',
      locale: !templateIdToUse && hasSwitchLocaleEnabled ? intl.locale : undefined,
      orientation,
      templateId: templateIdToUse
    });
  }, [validate, view, creationMethod, orientation, selectedTemplateId, reportName, hasSwitchLocaleEnabled, intl]);

  const handleView = useCallback(() => {
    if (view.state.newReportId) {
      onClose();
      navigate(getReportingEditReportUrl(workspaceId, organisationId, view.state.newReportId, isConsolidation));
    }
  }, [view.state.newReportId, navigate, workspaceId, organisationId, isConsolidation, onClose]);

  const handleNavigateToCreateTemplate = useCallback(() => {
    onClose();
    if (mode === 'dashboard') {
      dashboardTemplate.open();
    } else {
      template.open();
    }
  }, [onClose, mode, template, dashboardTemplate]);

  return (
    <>
      {screen === 'form' && (
        <ReportFormScreen
          error={error}
          validationErrors={validationErrors}
          reportName={reportName}
          creationMethod={creationMethod}
          orientation={orientation}
          selectedTemplateId={selectedTemplateId}
          workspaceTemplates={filteredWorkspaceTemplates}
          futrliTemplates={filteredFutrliTemplates}
          onReportNameChange={setReportName}
          onCreationMethodChange={setCreationMethod}
          onOrientationChange={setOrientation}
          onSelectedTemplateIdChange={setSelectedTemplateId}
          onClose={onClose}
          onCreate={handleCreate}
          onNavigateToCreateTemplate={handleNavigateToCreateTemplate}
          onBlur={revalidate}
          mode={mode}
        />
      )}
      {screen === 'creating' && <ReportCreatingScreen mode={mode} />}
      {screen === 'created' && <ReportCreatedScreen onClose={onClose} onView={handleView} mode={mode} />}
    </>
  );
};
